import {
  Button, Group, Panel,
  FormItem, Textarea, Header, AdaptivityProvider,
  SimpleCell, Switch, Caption, Skeleton
} from '@vkontakte/vkui';
import React, { useEffect, useRef, useState } from 'react';
import { Panels } from './Consts';
import { useAppContext } from '../AppContext';
import { ErrorLoadComponent } from './components/ErrorLoadComponent';
import * as Sentry from '@sentry/browser';
import { getResponseFromError, prepareString } from '../helpers/helpers';
import { AllowNotificationsComponent } from './components/AllowNotificationsComponent';
import { AppVersionComponent } from './components/AppVersionComponent';
import { PlatformPanelHeader } from './components/PlatformPanelHeader';
import { VkLimits } from './components/VkLimits';
import { useAppStore } from '../AppStore';

export const CURRENT_PANEL = Panels.PROFILE_PANEL;

const SkeletonPanel = () => {
  return (<Panel nav={CURRENT_PANEL}>
    <PlatformPanelHeader
      text={<Skeleton width={100} />}
    />
    <Group>
      <FormItem
        top={<Skeleton width={170} />}
        status={''}
        bottom={''}
      >
        <Skeleton width="100%" height={100} />
        <Caption level={'1'} className={'Profile__paymentInfoDescription'}>
          <Skeleton width="100%" />
        </Caption>
      </FormItem>
    </Group>

    <Group
      header={<Header
        mode="secondary"
      >
        <Skeleton width={100} />
      </Header>}
    >
      <AdaptivityProvider sizeY="compact">
        <SimpleCell
          Component="label"
          after={<Skeleton width={30} />}
        >
          <Skeleton width={190} />
        </SimpleCell>
        <SimpleCell
          Component="label"
          after={<Skeleton width={30} />}
        >
          <Skeleton width={85} />
        </SimpleCell>
      </AdaptivityProvider>
    </Group>
    <Group>
      <FormItem>
        <Skeleton width="100%" height={36} />
      </FormItem>
    </Group>
  </Panel>);
}

const ProfilePanel = (props) => {
  const context = useAppContext();
  const appStore = useAppStore();

  const [paymentInfo, setPaymentInfo] = useState('');
  const [paymentInfoError, setPaymentInfoError] = useState('');
  const [isLoadData, setIsLoadData] = useState(false);
  const [notifyUserJoinedEvent, setNotifyUserJoinedEvent] = useState(false);
  const [notifyUserAddedPayment, setNotifyUserAddedPayment] = useState(false);
  const [error, setError] = useState(null);
  const [isChanged, setIsChanged] = useState(false);
  const isFormSavedRef = useRef(false);

  const fetch = () => {
    setIsLoadData(true);
    context.init()
      .then(() => {
        context.httpClient()
          .profile()
          .then((response) => {
            setPaymentInfo(response.data.payment_info);
            setNotifyUserJoinedEvent(response.data.notify_user_joined_event);
            setNotifyUserAddedPayment(response.data.notify_user_added_payment);
            appStore.setRemainingReceiptScansCount(response.data.remaining_receipt_scans_count);
            appStore.setRemainingEventsCount(response.data.remaining_events_count);
            setError(null);
          })
          .catch((e) => {
            setError(e);
          })
          .finally(() => {
            setIsLoadData(false);
          });
    })
    .catch((e) => {
      setError(e);
    });
  }

  useEffect(() => {
    fetch();
  }, []);

  const save = () => {
    if (isFormSavedRef.current) {
      return;
    }

    isFormSavedRef.current = true;
    setIsLoadData(true);
    context.spinner(true);
    context.httpClient()
      .updateProfile({
        payment_info: prepareString(paymentInfo),
        notify_user_joined_event: notifyUserJoinedEvent,
        notify_user_added_payment: notifyUserAddedPayment,
      })
      .then((response) => {
        isFormSavedRef.current = false;
        setIsLoadData(false);
        setIsChanged(false);
        context.spinner(false);
      })
      .catch((error) => {
        context.spinner(false);
        setIsLoadData(false);
        isFormSavedRef.current = false;

        const response = getResponseFromError(error);
        if (!response) {
          Sentry.captureException(error);
          context.showError(error);
          return;
        }

        setPaymentInfoError(response.payment_info || '');
        delete response['payment_info'];
        delete response['notify_user_joined_event'];
        delete response['notify_user_added_payment'];

        if (Object.keys(response).length > 0) {
          setIsChanged(false);
          context.showError(error);
        } else {
          setIsChanged(true);
        }
      });

    context.getBridge().retargeting.addPaymentInfo(appStore.currentUserId);
  }

  if (error) {
    return (<ErrorLoadComponent
      error={error}
      onFetch={fetch}
    />)
  }

  if (isLoadData) {
    return (<SkeletonPanel />);
  }

  return (
    <Panel nav={CURRENT_PANEL}>
      <PlatformPanelHeader
        text={context.lang().getKey(CURRENT_PANEL, 'header')}
      />
      {context.snackbar}
      <Group>
        <FormItem
          top={context.lang().getKey(CURRENT_PANEL, 'payment_info_label')}
          status={paymentInfoError.length > 0 ? 'error' : ''}
          bottom={paymentInfoError}
        >
          <Textarea
            rows={4}
            placeholder={context.lang().getKey(CURRENT_PANEL, 'payment_info_placeholder')}
            value={paymentInfo}
            maxLength={120}
            onChange={(event) => {
              if ([...prepareString(event.target.value)].length > 120) {
                return;
              }

              setPaymentInfo(event.target.value);
              setPaymentInfoError('');
              setIsChanged(true);
            }}
            onBlur={() => setPaymentInfo(prepareString(paymentInfo))}
          />
          <Caption level={'1'} className={'Profile__paymentInfoDescription'}>{context.lang().getKey(CURRENT_PANEL, 'payment_info_description')}</Caption>
        </FormItem>
      </Group>

      <Group
        header={<Header
          mode="secondary"
        >
          {context.lang().getKey(CURRENT_PANEL, 'notifications_header')}
        </Header>}
      >
        <AdaptivityProvider sizeY="compact">
          {
            context.getBridge().notifications.get() ? (<>
              <SimpleCell
                Component="label"
                after={<Switch
                  checked={notifyUserJoinedEvent}
                  onChange={(e) => {
                    setNotifyUserJoinedEvent(e.target.checked);
                    setIsChanged(true);
                  }}
                />}
              >
                {context.lang().getKey(CURRENT_PANEL, 'notify_user_joined_event_label')}
              </SimpleCell>
              <SimpleCell
                Component="label"
                after={<Switch
                  checked={notifyUserAddedPayment}
                  onChange={(e) => {
                    setNotifyUserAddedPayment(e.target.checked);
                    setIsChanged(true);
                  }}
                />}
              >
                {context.lang().getKey(CURRENT_PANEL, 'notify_user_added_payment_label')}
              </SimpleCell>
            </>) : (<>
              <AllowNotificationsComponent
                checked={context.getBridge().notifications.get()}
                callback={(event) => {
                  context.getBridge().notifications.toggle(event.target.checked, (value) => {
                    fetch();
                  });
                }}
              />
            </>)
          }
        </AdaptivityProvider>
      </Group>
      <Group>
        <FormItem>
          <Button
            size="l"
            stretched
            loading={isLoadData}
            disabled={isLoadData || !isChanged}
            onClick={() => save()}
          >
            {context.lang().getKey('app', 'save_label')}
          </Button>
        </FormItem>
      </Group>
      <VkLimits />
      <AppVersionComponent />
    </Panel>
  );
}

export default ProfilePanel;
